import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GalleryImage, ImageSettings } from '@couch/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { GALLERY_KEY } from '../GalleryPage';
import { MACHINES_KEY } from '../machines/MachineActions';
import { archiveApi } from '../../api/storage';
import { useStorageConnected } from '../connections/useStorageConnected';

function Settings({ s }: { s: ImageSettings }) {
  const rows: [string, string | undefined][] = [
    ['Prompt', s.prompt], ['Negative', s.negative || undefined], ['Checkpoint', s.checkpoint],
    ['LoRAs', s.loras.length ? s.loras.map((l) => `${l.name}${l.strength === undefined ? '' : ` (${l.strength})`}`).join(', ') : undefined],
    ['Size', s.width && s.height ? `${s.width} × ${s.height}` : undefined], ['Seed', s.seed?.toString()], ['Steps', s.steps?.toString()],
    ['CFG', s.cfg?.toString()], ['Sampler', [s.sampler, s.scheduler].filter(Boolean).join(' / ') || undefined],
  ];
  return <dl className="wf-inputs">{rows.filter(([, v]) => v).map(([k, v]) => <div key={k}><dt>{k}</dt><dd className="wf-text">{v}</dd></div>)}</dl>;
}

const ARCHIVE_WORDS = { pending: 'On its way to your storage…', done: 'The original is in your storage.', failed: 'Saving the original to your storage failed.' } as const;

/** The original in the archive (doc 76): where it is, and "To archive" while the machine still has it. */
function ArchiveLine({ image, online }: { image: GalleryImage; online: boolean }) {
  const queryClient = useQueryClient();
  const connected = useStorageConnected();
  const save = useMutation({
    mutationFn: () => archiveApi.archiveImage(image.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...GALLERY_KEY, 'image', image.id] }),
  });
  if (!connected && !image.archive) return null;
  const canSave = connected && online && (image.archive === null || image.archive === 'failed');
  return (
    <p className="muted">
      {image.archive && ARCHIVE_WORDS[image.archive]}{' '}
      {canSave && <button type="button" className="button-quiet" disabled={save.isPending} onClick={() => { save.mutate(); }}>Keep the original in my storage</button>}
      {save.isError && <span role="alert" className="form-error"> {errorMessage(save.error)}</span>}
    </p>
  );
}

function Actions({ image, online }: { image: GalleryImage; online: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [fromMachine, setFromMachine] = useState(false);
  const again = useMutation({
    // Exactly the workflow that made this image — prompt overrides and seed included.
    mutationFn: () => api.rerun(image.runId ?? ''),
    onSuccess: () => navigate(`/workflows/${image.recipeId ?? ''}`),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteGalleryImage(image.id, fromMachine && online),
    onSuccess: async () => {
      // Drop the deleted image first: refetching it would 404 (with retries) before leaving the page.
      queryClient.removeQueries({ queryKey: [...GALLERY_KEY, 'image', image.id] });
      await navigate('/gallery');
      void queryClient.invalidateQueries({ queryKey: GALLERY_KEY });
    },
  });
  return (
    <div className="panel">
      <div className="machine-actions">
        {image.recipeId && image.runId && <button type="button" disabled={!online || again.isPending} onClick={() => { again.mutate(); }}>Run again</button>}
        {/* The full-size file from the machine, saved on this device (doc 61). */}
        {(online || image.archive === 'done') && <a className="button button-quiet" href={`${image.imageUrl}?download=1`} download={image.filename}>Download original</a>}
        <button type="button" className="button-quiet" disabled={remove.isPending} onClick={() => { if (window.confirm('Delete this image?')) remove.mutate(); }}>Delete</button>
        <label className="muted inline-check"><input type="checkbox" checked={fromMachine} disabled={!online} onChange={(e) => { setFromMachine(e.target.checked); }} /> also delete the file on the machine</label>
      </div>
      <ArchiveLine image={image} online={online} />
      {[again.error, remove.error].filter(Boolean).map((e, n) => <p key={n} role="alert" className="form-error">{errorMessage(e)}</p>)}
    </div>
  );
}

export function GalleryImagePage() {
  const { id = '' } = useParams();
  const detail = useQuery({ queryKey: [...GALLERY_KEY, 'image', id], queryFn: () => api.getGalleryImage(id) });
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines });
  if (detail.isPending) return <p role="status">Loading…</p>;
  if (detail.isError) return <p role="alert" className="form-error">{errorMessage(detail.error)} <Link to="/gallery">Back to the gallery</Link></p>;
  const { image } = detail.data;
  const machine = machines.data?.machines.find((m) => m.id === image.machineId);
  const online = machine?.online ?? false;
  // Offline or gone, an archived original still comes from the user's storage (doc 76).
  const full = online || image.archive === 'done';
  return (
    <section aria-labelledby="image-title">
      <p><Link to="/gallery">← Gallery</Link></p>
      <div className="page-head"><h1 id="image-title">{image.recipeTitle}</h1></div>
      <p className="muted">
        {new Date(image.createdAt).toLocaleString()} · {machine?.name ?? 'removed machine'}
        {image.recipeId && <> · <Link to={`/workflows/${image.recipeId}`}>Workflow{image.recipeVersion ? ` v${image.recipeVersion}` : ''}</Link></>}
      </p>
      <figure className="gallery-full">
        {full && image.kind === 'video' && <video src={image.imageUrl} controls playsInline preload="metadata" aria-label={image.filename} />}
        {full && image.kind === 'image' && <a href={image.imageUrl} target="_blank" rel="noreferrer"><img src={image.imageUrl} alt={image.filename} /></a>}
        {!full && image.hasThumb && <img src={image.thumbUrl} alt={image.filename} />}
        {!online && machines.isSuccess && (
          <figcaption className="notice">
            {machine ? `${machine.name} is offline` : 'The machine was removed'} — {full ? 'the original comes from your storage.' : 'this is the preview; the full image is on the machine.'}
          </figcaption>
        )}
      </figure>
      <Actions image={image} online={online} />
      <section className="panel" aria-labelledby="settings-title"><h2 id="settings-title">Settings</h2><Settings s={image.settings} /></section>
    </section>
  );
}
